import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { ThemeQuickToggle } from "./ThemeToggle";
import { getIdToken, isCoach, signOut } from "../lib/auth";
import { isPublicPath } from "../lib/publicPaths";
import { useUnreadMessages } from "../lib/queries";

interface NavItem {
  to: string;
  label: string;
  /** Whether an unread-message dot belongs on this tab -- the one place messages are reached from. */
  showsUnread?: boolean;
}

const COACH_NAV: NavItem[] = [
  // Messages hang off a client, so the client list is where an unread one is reachable from.
  { to: "/dashboard", label: "Clients", showsUnread: true },
  { to: "/library", label: "Library" },
  // The coach had no settings surface at all -- families had one, the coach didn't, so there was no way
  // to see your own account or change your own password.
  { to: "/coach/settings", label: "Account" },
  { to: "/help", label: "Help" },
];

const FAMILY_NAV: NavItem[] = [
  { to: "/family", label: "Training" },
  // "Has the coach said anything I need to act on?" is one of the four things a parent ever does here,
  // and it had no home in the navigation -- the only way in was a button on an athlete card, which at
  // phone width was the one hanging off the right edge of the screen (UX review F37). The unread dot
  // moved here with it, from Training, so it sits on the thing it is actually about.
  { to: "/family/messages", label: "Messages", showsUnread: true },
  { to: "/family/settings", label: "Account" },
  { to: "/help", label: "Help" },
];

/**
 * Persistent navigation.
 *
 * The prototype has a fixed tab bar and the built app never did: every screen carried its own ad-hoc
 * "Back to clients" / "Settings" link, so moving anywhere was a full page transition into a page whose
 * only way out was a differently-worded link somewhere else. That is most of what made the app feel
 * clunky -- there was no persistent sense of where you were or what else existed.
 *
 * Renders nothing at all when signed out. The login, signup and password-reset screens are standalone,
 * and showing tabs to somebody with no session would just be links that bounce them back to the login
 * page.
 */
export function NavBar() {
  const [items, setItems] = useState<NavItem[] | null>(null);
  const navigate = useNavigate();
  /**
   * Re-resolved on every navigation, not once on mount.
   *
   * This component lives outside <Routes>, so it mounts exactly once -- on the sign-in page, where
   * there is deliberately no session yet -- and never unmounts. Signing in is a client-side navigate(),
   * not a page load. So a mount-only effect asked "is there a session?" at the one moment the answer
   * was guaranteed to be no, cached that, and left the user with no navigation at all until they
   * happened to hard-refresh.
   *
   * The session check is local (the token is already in storage, no network call), so re-running it per
   * navigation is cheap.
   */
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let cancelled = false;
    if (isPublicPath(pathname)) {
      setItems([]);
      return;
    }
    getIdToken()
      .then(async (token) => {
        if (cancelled) return;
        if (!token) {
          setItems([]);
          return;
        }
        const coach = await isCoach();
        if (!cancelled) setItems(coach ? COACH_NAV : FAMILY_NAV);
      })
      // Signed out is the safe assumption if the session can't be read -- better to show no tabs than
      // tabs that lead nowhere.
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  // Navigating is the point of the menu, so it closes itself once you have.
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  /**
   * Escape, and a click anywhere outside. Both are what a dropdown is expected to do, and without them
   * an open menu on a phone can only be dismissed by hitting the same small button again.
   */
  useEffect(() => {
    if (!menuOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setMenuOpen(false);
    }
    function onPointerDown(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [menuOpen]);

  // Only asked for once there is a session; on the sign-in page there is nobody to have unread messages.
  const unread = useUnreadMessages(Boolean(items && items.length > 0));
  const hasUnread = (unread.data?.total ?? 0) > 0;

  function handleSignOut() {
    signOut();
    setItems([]);
    navigate("/signin");
  }

  if (!items || items.length === 0) return null;

  return (
    <nav className="nav-bar" aria-label="Main" ref={navRef}>
      <div className="nav-left">
        {/* Only rendered as a control on narrow screens (CSS); on desktop the tabs are simply visible
            and this is hidden, so there is no second navigation to keep in step. */}
        <button
          type="button"
          className="chip nav-menu-button"
          aria-expanded={menuOpen}
          aria-controls="app-nav-menu"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            {menuOpen ? (
              <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
            ) : (
              <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
            )}
          </svg>
        </button>
        <div className="nav-tabs" id="app-nav-menu" data-open={menuOpen}>
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            // `end` so /family doesn't stay highlighted while you're on /family/settings.
            end
            className={({ isActive }) => (isActive ? "nav-tab active" : "nav-tab")}
          >
            {item.label}
            {/* A dot, not a count. The question a nav badge answers is "is anything waiting", and a
                number here would have to be right about conversations the user is nowhere near. */}
            {item.showsUnread && hasUnread && <span className="nav-unread-dot" aria-label="Unread messages" />}
          </NavLink>
        ))}
        </div>
      </div>
      <div className="nav-actions">
        {/* A quick flip lives in the chrome; the full three-way choice, including "match my device",
            lives on the settings page where there is room to explain what it means. */}
        <ThemeQuickToggle />
        <button type="button" className="chip nav-signout" onClick={handleSignOut}>
          Sign out
        </button>
      </div>
    </nav>
  );
}
